import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const ABOUT_IMG = "https://images.unsplash.com/photo-1695956353120-54ce5e91632b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx1ZGFpcHVyJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4Njc5MDU2MHww&ixlib=rb-4.1.0&q=85";
const TEXTURE = "https://images.unsplash.com/photo-1773846012458-e6a66c26e49f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxibG9jayUyMHByaW50JTIwZmFicmljfGVufDB8fHx8MTc4Njc5MDU2MHww&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <div className="pt-32">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="overline text-[hsl(var(--primary))] mb-6">Our Heritage</p>
          <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-4xl">Nine colours of <span className="italic text-[hsl(var(--primary))]">Rajasthan</span>, printed by hand.</h1>
        </Reveal>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-16 items-center">
        <Reveal className="relative overflow-hidden aspect-[4/5]">
          <img src={ABOUT_IMG} alt="Udaipur" className="w-full h-full object-cover" />
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-body text-lg leading-relaxed text-foreground/80">
            Navrang Udaipur began with a simple belief — that the block-printing craft of Sanganer and Bagru deserves a place in the modern wardrobe.
          </p>
          <p className="font-body text-muted-foreground mt-6 leading-relaxed">
            Each shirt is printed by hand using teak wood blocks and natural dyes, then stitched from pure, breathable cotton. We make in small batches,
            directly with artisan families, so every piece carries the small imperfections that prove a human hand — not a machine — made it.
          </p>
          <div className="grid grid-cols-3 gap-6 mt-10 border-t border-border pt-8">
            {[["100%", "Cotton"], ["Natural", "Dyes"], ["Hand", "Printed"]].map(([a, b]) => (
              <div key={b}><p className="font-display text-3xl">{a}</p><p className="overline text-muted-foreground mt-1">{b}</p></div>
            ))}
          </div>
        </Reveal>
      </section>

      <Marquee words={["Sanganer", "Bagru", "Udaipur", "Handcrafted"]} className="bg-card" />

      {/* FOUNDERS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="overline text-[hsl(var(--primary))] mb-6">The People Behind Navrang</p>
          <h2 className="font-display text-4xl md:text-6xl max-w-3xl leading-tight">Founded by two brothers, bound by one <span className="italic text-[hsl(var(--primary))]">craft.</span></h2>
          <p className="font-body text-muted-foreground mt-8 max-w-2xl leading-relaxed">
            Navrang Udaipur was started by two partners — <strong className="text-foreground font-600">Pushpendra Singh Chundawat</strong> and <strong className="text-foreground font-600">Vipendra Singh Chundawat</strong>.
            Rooted in Udaipur and raised amid Rajasthan's living traditions, the duo set out with a shared dream: to bring the honest,
            hand block-printed Sanganeri craft to wardrobes across India — for men and children alike, at a fair and honest price.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-px bg-border border border-border mt-14">
          {[
            { name: "Pushpendra Singh Chundawat", role: "Co-Founder & Partner", quote: "Success is not final; failure is not fatal: it is the courage to continue that counts." },
            { name: "Vipendra Singh Chundawat", role: "Co-Founder & Partner", quote: "The best investment is in the tools of one's own trade." },
          ].map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1} className="bg-background p-8 md:p-12 flex flex-col">
              <h3 className="font-display text-3xl md:text-4xl leading-tight">{p.name}</h3>
              <p className="overline text-muted-foreground mt-3">{p.role}</p>
              <p className="font-display italic text-xl md:text-2xl text-foreground/80 mt-6 leading-snug border-l-2 border-[hsl(var(--primary))] pl-5">"{p.quote}"</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden py-28 text-center">
        <img src={TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl">Wear a piece of the craft.</h2>
            <Link to="/shop" className="mt-8 inline-flex items-center gap-3 bg-[hsl(var(--primary))] text-white px-8 py-4 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors">
              Shop now <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
